/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUiContext } from '../../../../../../context/UiContext';
import InsuranceService from '../../../../../../services/InsuranceService';
import useMutationWithConfirmation from '../../../../../../hooks/useMutationWithConfirmation';

const createRenewalInquiryDescription = ({
  insurance,
  canSendRenewalInquiry,
}) => {
  if (!canSendRenewalInquiry) {
    return 'A renewal inquiry can only be sent on an in force policy.';
  }

  if (!insurance) {
    return '';
  }

  if (insurance._agent_contact?.email) {
    return `Send insurance agent contact ${
      insurance._agent_contact?.name || ''
    } an email requesting renewal information.`;
  }

  return `Send insurance agent ${
    insurance._agent?.email || ''
  } an email requesting renewal information.`;
};

const createPayNowDescription = ({
  insurance,
  formValues,
  queryClient,
  canPayNow,
}) => {
  if (!canPayNow) {
    return 'This policy has already been paid.';
  }

  const vendors = queryClient.getQueryData(['vendors', 'Insurance']);

  let payee = '';
  let payeeFieldName = '';
  switch (insurance?.send_payment_to) {
    case 'Carrier':
      payeeFieldName = 'vendor_id';
      break;
    case 'Agent':
      payeeFieldName = 'agent_id';
      break;
    case 'Broker':
      payeeFieldName = 'broker_id';
      break;
    default:
      payeeFieldName = '';
      break;
  }

  payee = payeeFieldName
    ? vendors?.find((vendor) => vendor?.id === formValues[payeeFieldName])
        ?.name || insurance?.send_payment_to?.toLowerCase()
    : 'payee';

  if (!insurance.bill_id || !insurance.bill_id.length) {
    return `A bill for ${payee} will be created. A bill payment for ${payee} will also be created and applied to the new bill.`;
  }

  return `A bill payment for ${payee} will be created and applied to the unpaid bill for this policy.`;
};

export default function useInsuranceActions({
  insurance,
  isUnpaid,
  isEdit,
  readOnly,
  formValues,
  refetchLoan,
  refetchInsurance,
  loanId,
  removePolicy,
}) {
  const { showApiErrorToast, showSuccessToast } = useUiContext();

  const queryClient = useQueryClient();

  const canPayNow = isUnpaid && isEdit && !readOnly;
  const canRenew = !insurance?.insurance_next && !readOnly;
  const canSendNonRenewalNotice = insurance?.status === 'INFORCE' && !readOnly;
  const canSendRenewalInquiry = insurance?.status === 'INFORCE' && !readOnly;
  const canCreateNextBill = !insurance?.next_bill_id && !readOnly;
  const canBeDeleted =
    !readOnly &&
    isEdit &&
    (!insurance?.bill_id?.length ||
      !insurance?.bill_id.some((bill) => {
        return bill?.status === 'paid';
      }));

  const { mutate: payInsurance, isPending: isInsuranceBeingPaid } = useMutation(
    {
      mutationFn: () => InsuranceService.payInsurance(insurance?.id),
      onSuccess: async () => {
        showSuccessToast('Insurance has been scheduled for payment');
        await refetchLoan();
        refetchInsurance();
      },
      onError: (error) => showApiErrorToast(error),
    }
  );

  const { confirm: renew, isPending: isRenewing } = useMutationWithConfirmation(
    {
      confirmationMessage: 'Are you sure you want to renew this insurance?',
      successMessage: 'Insurance has been renewed for next year',
      mutationFn: () => InsuranceService.renewInsurance(insurance?.id),
      refetch: refetchInsurance,
    }
  );

  const {
    confirm: sendNonRenewalNotice,
    isPending: isSendingNonRenewalNotice,
  } = useMutationWithConfirmation({
    confirmationMessage:
      'This will send a non-renewal notice email to the borrower. This email informs the borrower that the insurance carrier is not renewing this policy. Are you sure you want to proceed?',
    successMessage: 'The email has been sent to the borrower',
    mutationFn: () => InsuranceService.createNonRenewalNotice(insurance?.id),
    refetch: refetchInsurance,
    confirmButtonLabel: 'Yes, send the email',
    cancelButtonLabel: 'No, cancel',
  });

  const { confirm: sendRenewalInquiry, isPending: isSendingRenewalInquiry } =
    useMutationWithConfirmation({
      confirmationMessage:
        insurance?._agent_contact && insurance?.agent_contact.email
          ? `This will send a renewal inquiry email to ${
              insurance?._agent_contact?.name || ''
            }. Are you sure you want to proceed?`
          : `This will send a renewal inquiry email to ${
              insurance?._agent?.name || ''
            }. Are you sure you want to proceed?`,
      successMessage: 'Insurance renewal inquiry sent successfully',
      mutationFn: () =>
        InsuranceService.createInsuranceRenewalInquiry(insurance?.id),
      refetch: refetchInsurance,
      confirmButtonLabel: 'Yes, send the email',
      cancelButtonLabel: 'No, cancel',
    });

  const { mutate: createNextBill } = useMutation({
    mutationFn: () => {
      return InsuranceService.createNextInsuranceBill(insurance?.id);
    },
    onSuccess: async () => {
      showSuccessToast('Success');
      await refetchInsurance();
      queryClient.invalidateQueries({ queryKey: ['loanBillOptions', loanId] });
    },
    onError: (error) => {
      showApiErrorToast(error);
    },
  });

  const actions = useMemo(() => {
    if (!insurance) {
      return [];
    }

    return [
      {
        actionTitle: 'Pay now',
        actionDescription: createPayNowDescription({
          insurance,
          formValues,
          queryClient,
          canPayNow,
        }),
        disabled: !canPayNow || isInsuranceBeingPaid,
        onClick: payInsurance,
      },
      {
        actionTitle: 'Renew policy',
        actionDescription: canRenew
          ? "Create an identical policy with coverage starting the day after this policy's expiration."
          : 'This policy cannot be renewed because there is already a renewed policy in place.',
        disabled: !canRenew || isRenewing,
        onClick: renew,
      },
      {
        actionTitle: 'Non-Renewal Notice',
        actionDescription: canSendNonRenewalNotice
          ? 'Send the borrower a notice informing them that their insurance carrier will not renew their policy.'
          : 'A non-renewal notice to the borrower can only be sent if the policy is in force.',
        disabled: !canSendNonRenewalNotice || isSendingNonRenewalNotice,
        onClick: sendNonRenewalNotice,
      },
      {
        actionTitle: 'Renewal Inquiry',
        actionDescription: createRenewalInquiryDescription({
          insurance,
          canSendRenewalInquiry,
        }),
        disabled: !canSendRenewalInquiry || isSendingRenewalInquiry,
        onClick: sendRenewalInquiry,
      },
      {
        actionTitle: 'Create Next Bill',
        actionDescription: canCreateNextBill
          ? 'Create the next bill for this policy. The next bill is required for properly calculating the impound schedule.'
          : 'This insurance policy already has the next bill setup.',
        disabled: !canCreateNextBill,
        onClick: createNextBill,
      },
      {
        actionTitle: 'Delete insurance policy',
        actionDescription: canBeDeleted
          ? 'Delete this insurance policy.'
          : 'This policy cannot be deleted because it has already been paid.',
        disabled: !canBeDeleted,
        icon: (
          <span className="text-red-500">
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
              <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
            </svg>
          </span>
        ),
        onClick: removePolicy,
      },
    ];
  }, [
    canBeDeleted,
    canCreateNextBill,
    canPayNow,
    canRenew,
    canSendNonRenewalNotice,
    canSendRenewalInquiry,
    createNextBill,
    formValues,
    insurance,
    isInsuranceBeingPaid,
    isRenewing,
    isSendingNonRenewalNotice,
    isSendingRenewalInquiry,
    payInsurance,
    queryClient,
    removePolicy,
    renew,
    sendNonRenewalNotice,
    sendRenewalInquiry,
  ]);

  return actions;
}
